import React from 'react';

/**
 * Organization Schema Markup Generator
 * Generates JSON-LD structured data for the organization
 */

export interface OrganizationSchemaData {
  name: string;
  url: string;
  logo: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  sameAs?: string[]; // Social media profiles
}

export function generateOrganizationSchema(data: OrganizationSchemaData) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: data.name,
    url: data.url,
    logo: data.logo,
  };

  if (data.description) {
    schema.description = data.description;
  }

  if (data.email) {
    schema.email = data.email;
  }

  if (data.phone) {
    schema.telephone = data.phone;
  }

  if (data.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: data.address.streetAddress,
      addressLocality: data.address.addressLocality,
      addressRegion: data.address.addressRegion,
      postalCode: data.address.postalCode,
      addressCountry: data.address.addressCountry,
    };
  }

  if (data.sameAs && data.sameAs.length > 0) {
    schema.sameAs = data.sameAs;
  }

  return schema;
}

/**
 * LocalBusiness Schema (extends Organization)
 */
export function generateLocalBusinessSchema(data: OrganizationSchemaData) {
  const schema = generateOrganizationSchema(data);
  schema['@type'] = 'LocalBusiness';

  // Add business-specific properties
  schema.priceRange = '$$';

  return schema;
}

/**
 * Generate Organization JSON-LD script tag
 */
export function OrganizationSchemaScript({ data }: { data: OrganizationSchemaData }) {
  const schema = generateOrganizationSchema(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/**
 * Generate LocalBusiness JSON-LD script tag
 */
export function LocalBusinessSchemaScript({ data }: { data: OrganizationSchemaData }) {
  const schema = generateLocalBusinessSchema(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
